import React, { useMemo } from "react";
import { Target, TrendingUp } from "lucide-react";
import { Box, Card, Stack, Typography } from "@mui/material";

function toTitleCase(value) {
	if (!value) return "";
	return String(value)
		.split(" ")
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
}

function getLabelFromRecommendation(item) {
	if (!item) return "";
	return (
		item.topic_name ||
		item.specialty_name ||
		item.name ||
		item.label ||
		""
	);
}

export default function RecomendacionesReforzar({
	data,
	recommendations,
	title = "Te recomendamos reforzar",
	helperText = "Son los temas con más errores en tus últimas prácticas.",
	emptyText = "Aún no hay suficientes datos para recomendar temas.",
}) {
	const rawRecommendations = useMemo(() => {
		if (Array.isArray(recommendations) && recommendations.length > 0) {
			return recommendations;
		}

		if (Array.isArray(data?.recommendations) && data.recommendations.length > 0) {
			return data.recommendations;
		}

		return [];
	}, [data, recommendations]);

	const recommendedLabels = useMemo(() => {
		const explicit = rawRecommendations
			.map(getLabelFromRecommendation)
			.filter(Boolean)
			.slice(0, 2)
			.map(toTitleCase);

		if (explicit.length > 0) {
			return explicit;
		}

		// Fallback when recommendations are empty: use specialties with more content.
		const fallback = (data?.catalog?.specialties || [])
			.filter((specialty) => Number(specialty?.question_count) > 0)
			.sort((a, b) => Number(b?.question_count || 0) - Number(a?.question_count || 0))
			.slice(0, 2)
			.map((specialty) => toTitleCase(specialty?.name));

		return fallback;
	}, [data, rawRecommendations]);

	const hasRecommendations = recommendedLabels.length > 0;

	return (
		<Card
			elevation={0}
			sx={{
				borderRadius: "0.75rem",
				border: "1px solid",
				borderColor: "#e5e7eb",
				bgcolor: "#eef1f7",
				p: 2,
			}}
		>
			<Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
				<Box sx={{ flex: 1 }}>
					<Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
						{title}
					</Typography>

					<Typography sx={{ mt: 0.75, fontSize: "1.35rem", fontWeight: 700, color: "#2563eb", lineHeight: 1.15 }}>
						{hasRecommendations ? recommendedLabels.join(" y ") : "Sin recomendaciones"}
					</Typography>

					<Typography sx={{ mt: 0.75, fontSize: "0.9rem", color: "#334155", lineHeight: 1.35 }}>
						{hasRecommendations ? helperText : emptyText}
					</Typography>
				</Box>

				<Box
					sx={{
						mt: 0.5,
						width: 56,
						height: 56,
						borderRadius: "999px",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						bgcolor: "#e3e9ff",
						color: "#2563eb",
						flexShrink: 0,
					}}
				>
					{hasRecommendations ? <TrendingUp size={24} /> : <Target size={22} />}
				</Box>
			</Stack>
		</Card>
	);
}
